import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useCustomers } from "../../context/CustomerContext";

export default function ComposeMail() {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { customers } = useCustomers();

    const [recipients, setRecipients] = useState([]);
    const [recipientInput, setRecipientInput] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);

    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    const [sampleSize, setSampleSize] = useState(100);

    useEffect(() => {
        if (!recipientInput.trim()) {
            setFilteredCustomers([]);
            return;
        }
        const val = recipientInput.toLowerCase();
        setFilteredCustomers(
            customers.filter(c =>
                c.name.toLowerCase().includes(val) || c.email.toLowerCase().includes(val)
            )
        );
    }, [recipientInput, customers]);

    function addRecipient(customer) {
        const value = customer.email;
        if (!value || recipients.some(r => r.value === value)) return;
        setRecipients(prev => [...prev, { name: customer.name, value, raw: customer }]);
        setRecipientInput("");
        setFilteredCustomers([]);
    }

    function addTypedRecipient() {
        const raw = recipientInput.trim();
        if (!raw) return;
        if (!raw.includes("@")) {
            toast.error("Invalid email format");
            return;
        }
        addRecipient({ name: raw, email: raw });
    }

    function handleRecipientKey(e) {
        if (["Enter", "Tab"].includes(e.key)) {
            e.preventDefault();
            addTypedRecipient();
        }
        if (e.key === ",") {
            e.preventDefault();
            addTypedRecipient();
        }
    }

    function removeRecipient(value) {
        setRecipients(prev => prev.filter(r => r.value !== value));
    }

    function onFilesSelected(e) {
        const files = Array.from(e.target.files);
        setAttachments(prev =>
            [...prev, ...files.map(f => ({ id: Math.random().toString(36).slice(2), name: f.name, file: f }))]
        );
        e.target.value = null;
    }

    function removeAttachment(id) {
        setAttachments(prev => prev.filter(a => a.id !== id));
    }

    async function handleSend() {
        if (!baseUrl) {
            toast.error("Base URL missing");
            return;
        }

        if (sampleSize < recipients.length) {
            toast.error(
                `Number of customers must be at least equal to selected recipients (${recipients.length}).`
            );
            return;
        }

        const formData = new FormData();
        const formattedHtml = body.replace(/\n/g, "<br>");
        const fromName = localStorage.getItem("selectedOutlet") || localStorage.getItem("selectedName") || "";

        formData.append("from_name", fromName);
        formData.append("subject", subject);
        formData.append("html", formattedHtml);
        formData.append("sample_size", sampleSize.toString() || '0');

        const allRecipients = [
            ...recipients.map(r => ({ name: r.name, email: r.value })),
            ...recipientInput
                .split(",")
                .map(e => e.trim())
                .filter(e => e.includes("@"))
                .map(e => ({ name: e, email: e }))
        ];

        formData.append("additional_emails", JSON.stringify(allRecipients));

        attachments.forEach(a => formData.append("attachments", a.file));

        try {
            await axios.post(`${baseUrl}/send-email`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Email sent successfully");

            setRecipients([]);
            setRecipientInput("");
            setSubject("");
            setBody("");
            setAttachments([]);
            setSampleSize(100);
        } catch (err) {
            console.error(err);
            toast.error("Failed to send email");
        }
    }

    return (
        <div className="p-5 bg-white shadow rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Bulk Mail</h2>

            <div className="mb-4">
                <label className="text-sm text-gray-600">To</label>
                <div className="border rounded px-2 py-2 mt-1 relative">
                    <div className="flex flex-wrap gap-2">
                        {recipients.map(r => (
                            <span key={r.value} className="flex items-center gap-2 bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-sm">
                                {r.name} ({r.value})
                                <button onClick={() => removeRecipient(r.value)} className="text-xs">✕</button>
                            </span>
                        ))}

                        <input
                            value={recipientInput}
                            onChange={e => setRecipientInput(e.target.value)}
                            onKeyDown={handleRecipientKey}
                            placeholder="Type name or email"
                            className="flex-1 min-w-[180px] outline-none px-1 py-1"
                        />
                    </div>

                    {filteredCustomers.length > 0 && (
                        <div className="absolute left-0 right-0 top-full bg-white border shadow mt-1 rounded z-10 max-h-48 overflow-auto">
                            {filteredCustomers.map(c => (
                                <div key={c.id} onClick={() => addRecipient(c)} className="px-3 py-2 hover:bg-indigo-50 cursor-pointer">
                                    <div className="font-medium">{c.name}</div>
                                    <div className="text-sm text-gray-600">{c.email}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="mb-4">
                <label className="text-sm text-gray-600">Number of customers</label>
                <input
                    type="number"
                    min="1"
                    value={sampleSize}
                    onChange={e => setSampleSize(e.target.value)}
                    className="w-full border rounded px-3 py-2 mt-1"
                />
            </div>

            <div className="mb-4">
                <label className="text-sm text-gray-600">Subject</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} className="w-full border rounded px-3 py-2 mt-1" />
            </div>

            <div className="mb-2">
                <label className="text-sm text-gray-600">Body</label>
                <textarea rows={8} value={body} onChange={e => setBody(e.target.value)} className="w-full border rounded px-3 py-2 mt-1" />
            </div>

            <div className="mb-4">
                <label className="text-sm text-gray-600">Attachments</label>
                <div className="flex items-center gap-2 mt-1">
                    <input ref={fileInputRef} multiple type="file" className="hidden" onChange={onFilesSelected} id="file-input-mail" />
                    <label htmlFor="file-input-mail" className="cursor-pointer px-3 py-1 border rounded bg-gray-50 hover:bg-indigo-50 text-sm">Choose Files</label>
                    <span className="text-sm text-gray-500">{attachments.length} files</span>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                    {attachments.map(a => (
                        <div key={a.id} className="px-2 py-1 bg-gray-100 rounded text-sm flex items-center gap-2">
                            {a.name}
                            <button onClick={() => removeAttachment(a.id)} className="text-xs">✕</button>
                        </div>
                    ))}
                </div>
            </div>

            <button
                onClick={handleSend}
                className="px-4 py-2 rounded-sm bg-purple-100 text-purple-800 hover:bg-purple-200 text-sm"
            >
                Send Mail
            </button>
        </div>
    );
}
